// src/components/GameTargets.jsx
import React, {useState, useMemo, useEffect, useRef} from 'react';
import {Instance, Instances, useGLTF} from '@react-three/drei'; // Import useGLTF
import {useFrame} from '@react-three/fiber';
import * as THREE from 'three';
import {useGameStore} from '../stores/gameStore';
import {Howl} from 'howler';
import {targetPositionsStart, targetPositionsEnd, targetsAxis, targetsAxisNumber} from '../utils/constants.jsx' // Import Howl

// --- Initialize Howl instance once using useMemo or outside the component ---
// Option A: Outside component (if path is static and sound is always needed when component is used)
// const hitSound = new Howl({
//   src: ['./music/target_break.wav'], // Adjust path
//   volume: 0.2,
//   // preload: true, // Howler preloads by default unless you set it to false
// });

// Option B: Inside component using useMemo (good if src might change or for encapsulation)
const useHitSoundHowler = () => {
    const hitSound = useMemo(() => {
        console.log("Creating Howl for hit sound");
        return new Howl({
            src: ['./music/target_break.mp3'], // Adjust path
            volume: 0.2,
            // html5: true, // Force HTML5 if needed, but Web Audio is usually better for SFX
        });
    }, []); // Empty dependency array means it's created once

    // Optional: Unload sound when component unmounts to free resources
    useEffect(() => {
        return () => {
            if (hitSound) {
                // console.log("Unloading hit sound");
                // hitSound.unload(); // Be careful with this if other components might share this Howl instance
            }
        };
    }, [hitSound]);


    const play = () => {
        if (hitSound) {
            hitSound.play(); // Howler handles overlapping plays efficiently
        }
    };
    return play;
};

const INITIAL_TARGET_COUNT = 10;
const randomInRange = (min, max) => Math.random() * (max - min) + min;

// Preload your custom model
useGLTF.preload('./models/target_s.glb'); // IMPORTANT: Adjust path

// SingleTarget component remains largely the same
function SingleTarget({id, currentPosition, currentRotation, onTargetClick, modelParts, modelScale}) {
    const groupRef = useRef(null);

    const handleClick = (event) => {
        event.stopPropagation();
        // The click event will be on one of the inner meshes.
        // We rely on the onTargetClick callback with the 'id' of the whole target unit.
        onTargetClick(id);
    };

    // If modelParts are not loaded, don't render
    if (!modelParts || modelParts.length === 0) return null;

    return (
        // The <Instance> component from drei will apply its transform props
        // to this entire <group>. So, the group (and its children) will be positioned/rotated.
        <Instance
            ref={groupRef} // Optional: ref on the Instance itself if needed by <Instances> internals
            position={currentPosition}
            rotation={currentRotation}
            scale={modelScale || 1} // Apply overall scale to the instance
            // The onClick here might not be ideal if you want to know WHICH part was clicked.
            // It's better to put onClick on the individual meshes inside the group if needed,
            // but for a simple "target hit", onClick on Instance is fine.
            // For this example, let's assume a click on any part counts as hitting the target ID.
            onClick={handleClick}
        >
            {/* This group is what actually gets instanced effectively.
                The Instance component above controls the transform of this group.
                However, for click events on individual parts to propagate the 'id',
                you might need to put onClick handlers on the meshes below
                and have them call onTargetClick(id).
                For simplicity here, the Instance's onClick handles it.
            */}
            {modelParts.map((part, index) => (
                <mesh
                    key={index}
                    geometry={part.geometry}
                    material={part.material}
                    // You can add individual onClick handlers here if you need to differentiate
                    // which part of the compound target was hit, but then you'd call
                    // onTargetClick(id) from these handlers.
                    // onClick={(e) => { e.stopPropagation(); onTargetClick(id); }}
                >
                    {/* If parts need individual outlines, add them here.
                        But usually, you'd outline the whole compound object.
                        <Outlines />
                    */}
                </mesh>
            ))}
        </Instance>
    );
}

export function GameTargets() {
    const {incrementScore, endGame} = useGameStore.getState();
    const isPlaying = useGameStore((state) => state.gameState === 'playing');
    const [targets, setTargets] = useState([]);
    const targetMovementData = useRef([]); // This will store movement parameters for each target

    const playHitSound = useHitSoundHowler()

    const {nodes, materials: modelMaterials} = useGLTF('./models/target_s.glb');
    // --- Extract geometries and materials for the two parts ---
    // Replace 'MeshPart1NodeName' and 'MeshPart2NodeName'
    // Replace 'MaterialForPart1' and 'MaterialForPart2'
    const modelDefinition = useMemo(() => {
        if (nodes.target_1 && nodes.target_2) {
            return {
                parts: [
                    {
                        geometry: nodes.target_1.geometry,
                        // Option A: Use a new material
                        material: new THREE.MeshBasicMaterial({color: 'red'}),
                        // Option B: Try to use material from GLB
                        // material: modelMaterials.MaterialForPart1 || new THREE.MeshStandardMaterial({ color: 'red' }),
                    },
                    {
                        geometry: nodes.target_2.geometry,
                        // Option A: Use a new material
                        material: new THREE.MeshBasicMaterial({color: 'white'}),
                        // Option B: Try to use material from GLB
                        // material: modelMaterials.MaterialForPart2 || new THREE.MeshStandardMaterial({ color: 'blue' }),
                    },
                ],
                // Define a base material and geometry for the <Instances> component.
                // This is a bit of a quirk with <Instances> when children define their own.
                // We can provide a minimal, non-rendered one or the geometry of the main part.
                baseGeometry: new THREE.BufferGeometry(), // Empty geometry, as children render
                baseMaterial: new THREE.MeshBasicMaterial({visible: false}), // Invisible material
                // OR, if one part is "dominant":
                // baseGeometry: nodes.MeshPart1NodeName.geometry,
                // baseMaterial: new THREE.MeshStandardMaterial({ color: 'purple' }) // This material will be used by <Instances>
            };
        }
        console.error("One or both target mesh parts not found in GLB. Using fallback.");
        // Fallback if model parts are not found
        const fallbackBox = new THREE.BoxGeometry(0.8, 0.8, 0.8);
        const fallbackMat = new THREE.MeshBasicMaterial({color: 'grey'});
        return {
            parts: [{geometry: fallbackBox, material: fallbackMat}],
            baseGeometry: fallbackBox,
            baseMaterial: fallbackMat,
        };
    }, [nodes, modelMaterials]);


    const initializeTargets = () => {
        console.log("GameTargets: Initializing targets.");
        const newTargetsData = [];
        const newMovementData = []; // Create a new array for movement data

        for (let index = 0; index < INITIAL_TARGET_COUNT; index++) {
            newTargetsData.push({
                id: index,
                // position: new THREE.Vector3(-8 + index * 2, 5, 0),
                position: new THREE.Vector3(...targetPositionsStart[index]),
                rotation: new THREE.Euler(0, 0, 0),
                isActive: true,
                modelScale: 3,
            });
            // Initialize corresponding movement data for this target
            newMovementData.push({
                positionSpeed: randomInRange(0.01, 0.03) * (Math.random() < 0.5 ? 1 : -1),
                // xAngleSpeed: randomInRange(0.005, 0.02) * (Math.random() < 0.5 ? 1 : -1), // Re-enable if needed
                rotationSpeed: randomInRange(0.04, 0.05) * (Math.random() < 0.5 ? 1 : -1),
                // zAngleSpeed: randomInRange(0.005, 0.02) * (Math.random() < 0.5 ? 1 : -1), // Re-enable if needed
                axis: targetsAxis[index], // Use the axis from constants
                bounds: [Math.min(targetPositionsStart[index][targetsAxisNumber[index]], targetPositionsEnd[index][targetsAxisNumber[index]])
                    , Math.max(targetPositionsStart[index][targetsAxisNumber[index]], targetPositionsEnd[index][targetsAxisNumber[index]])],
            });

        }
        console.log(newMovementData.map(m => m.bounds));
        setTargets(newTargetsData);
        targetMovementData.current = newMovementData; // Assign the newly created movement data
    };

    // useEffect for isPlaying (initializeTargets) - no change from previous
    useEffect(() => {
        if (isPlaying) {
            initializeTargets();
        } else {
            setTargets([]);
            targetMovementData.current = []; // Also clear movement data
        }
    }, [isPlaying]);

    // handleTargetClick - no change from previous
    const handleTargetClick = (clickedId) => {
        if (!isPlaying) return;

        playHitSound(); // Play sound using Howler

        incrementScore(1);
        setTargets(prevTargets =>
            prevTargets.map(target =>
                target.id === clickedId ? {...target, isActive: false} : target
            )
        );
    };

    // useEffect for checking if all targets hit - no change from previous
    useEffect(() => {
        if (!isPlaying || targets.length === 0) return;
        const activeTargets = targets.filter(target => target.isActive);
        if (activeTargets.length === 0 && INITIAL_TARGET_COUNT > 0) { // Ensure we had targets to begin with
            console.log("All targets cleared!");
            endGame();
        }
    }, [targets, isPlaying, endGame]);

    // useFrame for per-frame updates - no change in logic, only data being updated

    useFrame((state, delta) => {
        if (!isPlaying || targets.length === 0 || targetMovementData.current.length === 0) return;

        setTargets(prevTargets =>
            prevTargets.map((target, index) => {
                if (!target.isActive) return target;

                // Access movement data by index, ensure it exists
                const movement = targetMovementData.current[index];
                if (!movement) {
                    // This case should ideally not happen if initialization is correct
                    // console.warn(`No movement data for target index ${index}`);
                    return target;
                }

                const newPosition = target.position.clone();
                newPosition[movement.axis] += movement.positionSpeed * (60 * delta); // Apply delta to position changes too for consistency
                if (newPosition[movement.axis] > movement.bounds[1] || newPosition[movement.axis] < movement.bounds[0]) {
                    movement.positionSpeed *= -1;
                    newPosition[movement.axis] = THREE.MathUtils.clamp(newPosition[movement.axis], movement.bounds[0], movement.bounds[1]);
                }

                const newRotation = target.rotation.clone();
                newRotation.y += movement.rotationSpeed * (60 * delta);

                return {
                    ...target,
                    position: newPosition,
                    // rotation: newRotation,
                };
            })
        );
    });


    if (!isPlaying || targets.length === 0 || !modelDefinition.parts.length) {
        return null;
    }

    return (
        <Instances
            geometry={modelDefinition.baseGeometry}
            material={modelDefinition.baseMaterial}
            limit={INITIAL_TARGET_COUNT}
        >
            {targets.map(target =>
                target.isActive ? (
                    <SingleTarget
                        key={target.id}
                        id={target.id}
                        currentPosition={target.position}
                        currentRotation={target.rotation}
                        onTargetClick={handleTargetClick}
                        modelParts={modelDefinition.parts}
                        modelScale={target.modelScale}
                    />
                ) : null
            )}
        </Instances>
    );
}